import { useVbsWorkOrderCreateMutation } from "@/services/backend/vbs/work-orders/api/work-order-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { v4 } from "uuid";
import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { BranchSelectFrom } from "@/services/backend/company/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

interface Props {
  processId: string;
  onCreated: (workOrderId: string) => void;
}

export function CreateWorkOrderButton({ processId, onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [create, { isLoading, error, reset }] = useVbsWorkOrderCreateMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    !process ||
    !process.customerId ||
    isLoading ||
    branchId === "" ||
    serviceObjectId === "";

  const { toast } = useToast();

  if (error) {
    toast({
      ...parseRTKQueryError(error),
    });
    reset();
  }

  const doCreate = () => {
    if (disabled) {
      return;
    }

    const id = v4();
    create({
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then(() => {
        onCreated(id);
      })
      .catch(() => null);
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        <Plus />
        <span>{t("Arbeitsschein")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} size="sm">
          <Plus />
          <span>{t("Arbeitsschein")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Arbeitsschein erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
